import React, { useState } from 'react';
import axios from 'axios';
import './EmailCard.css';

function EmailCard({ email, isExpanded, onToggle }) {
  const [deleting, setDeleting] = useState(false);
  const [reading, setReading] = useState(false);

  const handleMarkAsRead = async () => {
    setReading(true);
    try {
      await axios.patch(`/api/emails/${email._id}/read`);
    } catch (error) {
      console.error('Error marking as read:', error);
    }
    setReading(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`/api/emails/${email._id}`);
      // Optionally refresh the list
      window.location.reload();
    } catch (error) {
      console.error('Error deleting email:', error);
    }
    setDeleting(false);
  };

  const getCategoryColor = (category) => {
    const colors = {
      financial: '#FF6B6B',
      security: '#FFD93D',
      shopping: '#6BCB77',
      marketing: '#4D96FF',
      general: '#999'
    };
    return colors[category] || colors.general;
  };

  const formatDate = (date) => {
    const d = new Date(date);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const renderEmailBody = (body) => {
    // Extract image URLs
    const imgRegex = /<img[^>]+src="([^">]+)"/g;
    const videoRegex = /<video[^>]+src="([^">]+)"/g;
    
    let content = body;
    const images = [];
    const videos = [];
    
    let match;
    while ((match = imgRegex.exec(body)) !== null) {
      images.push(match[1]);
    }
    while ((match = videoRegex.exec(body)) !== null) {
      videos.push(match[1]);
    }

    return (
      <div className="email-body-content">
        <div dangerouslySetInnerHTML={{ __html: content.substring(0, 500) }} />
        {images.length > 0 && (
          <div className="email-media">
            {images.slice(0, 3).map((src, idx) => (
              <img key={idx} src={src} alt="" className="email-image" />
            ))}
          </div>
        )}
        {videos.length > 0 && (
          <div className="email-media">
            {videos.slice(0, 2).map((src, idx) => (
              <video key={idx} src={src} controls className="email-video" />
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderAttachments = (attachments = []) => {
    if (!attachments.length) return null;

    return (
      <div className="email-attachments">
        <h4>Attachments</h4>
        <div className="attachment-grid">
          {attachments.map((attachment) => {
            const downloadUrl = `/api/emails/${email._id}/attachments/${attachment.attachmentId}`;
            const isImage = attachment.mimeType?.startsWith('image/');
            const isAudio = attachment.mimeType?.startsWith('audio/');

            return (
              <div key={attachment.attachmentId} className="attachment-card">
                {isImage && (
                  <img src={downloadUrl} alt={attachment.filename} className="attachment-preview" />
                )}
                {isAudio && (
                  <audio controls src={downloadUrl} className="attachment-audio" />
                )}
                <div className="attachment-meta">
                  <span className="attachment-name">{attachment.filename || 'Attachment'}</span>
                  <span className="attachment-type">{attachment.mimeType || 'file'}</span>
                  <a href={downloadUrl} target="_blank" rel="noopener noreferrer">
                    Download
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={`email-card ${isExpanded ? 'expanded' : ''} ${email.isRead ? 'read' : 'unread'}`}>
      <div className="email-header" onClick={onToggle}>
        <div className="email-left">
          <div className="email-from">{email.from}</div>
          <div className="email-subject">{email.subject}</div>
        </div>
        <div className="email-right">
          <div className="email-badges">
            {email.isCoupon && (
              <span className="badge coupon-badge" title="This is a coupon/deal">
                🎟️ Coupon
              </span>
            )}
            <span 
              className="badge category-badge"
              style={{ backgroundColor: getCategoryColor(email.category) }}
              title={`Category: ${email.category}`}
            >
              {email.category}
            </span>
          </div>
          <div className="email-date">{formatDate(email.date)}</div>
        </div>
      </div>

      {isExpanded && (
        <div className="email-body">
          {renderEmailBody(email.body || '')}
          {renderAttachments(email.attachments)}
          <div className="email-actions">
            {!email.isRead && (
              <button 
                className="action-btn read-btn"
                onClick={handleMarkAsRead}
                disabled={reading}
              >
                {reading ? '⏳' : '✓'} Mark as Read
              </button>
            )}
            <button 
              className="action-btn delete-btn"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? '⏳' : '🗑️'} Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmailCard;
